<template>
  <div class="task-group" v-if="currentUserProfile">
    <div
      v-for="task in taskGroup.tasks"
      :id="`task-${task.step + 1}`"
      :key="task.step"
      :class="['task-group__task', { current: task.step === currentUserProfile.timelineStep, complete: isTaskComplete(task) }]"
      >
      <i :class="getTaskIconClass(task)"></i>
      <div v-html="`${task.step + 1}. ${isTaskComplete(task) ? task.done : task.text}`"/>
    </div>
    <div class="task-group__actions" v-if="showActions && taskGroup.actions && (taskGroup.actions.some(a => a.step === currentUserProfile.timelineStep) || taskGroup.actions.some(a => a.sticky === true))">
      <button
        v-for="action in taskGroup.actions"
        :key="action.text"
        @click="callFunction(action.method)"
        :disabled="pending"
        v-show="shouldShowAction(action)"
        >
        {{ action.text }}
      </button>
    </div>
  </div>
</template>

<script>
import * as fb from '@/library/firebase'
import { mapActions, mapGetters, mapState } from 'vuex'
import router from '@/router'
const sendEmail = fb.funcs.httpsCallable('sendEmail')

export default {
  name: 'TimelineTask',
  props: {
    taskGroup: Object,
    showActions: Boolean,
    consultationId: String
  },
  data() {
    return {
      pending: false
    }
  },
  computed: {
    ...mapGetters([
      'currentUserProfile'
    ]),
    ...mapState([
      'adminEmail',
      'isAdmin',
      'userProfile'
    ])
  },
  methods: {
    ...mapActions([
      'setTimelineStep'
    ]),
    shouldShowAction(action) {
      const stepMatches = action.step === this.currentUserProfile?.timelineStep
      if (action.condition) {
        return stepMatches && action.condition && this.callFunction(action.condition)
      }
      return stepMatches
    },
    callFunction(functionName) {
      if (typeof this[functionName] === 'function') {
        return this[functionName]()
      }
      else {
        console.error(`Function "${functionName}" is not defined`)
      }
    },
    getTaskIconClass(message) {
      return this.isTaskComplete(message) ? 'checked' : 'unchecked'
    },
    isTaskComplete(message) {
      return this.currentUserProfile?.timelineStep > message.step
    },
    markAsFiled() {
      this.setTimelineStep(12);
    },
    // Task functions below
    shouldShowBookingDetails() {
      return Boolean(this.consultationId) && this.currentUserProfile?.timelineStep < 3
    },
    isUserAdmin() {
      return this.isAdmin;
    },
    shouldShowAddClientNumberButton() {
      return this.isAdmin && !this.currentUserProfile?.clientNumber;
    },
    shouldShowUploadFilingReceiptButton() {
      return this.isAdmin && !this.currentUserProfile?.application?.filingReceipt;
    },
    shouldShowUploadAsFiledButton() {
      return this.isAdmin && !this.currentUserProfile?.application?.asFiled;
    },
    shouldShowAddSerialNumberButton() {
      return this.isAdmin && !this.currentUserProfile?.application?.serialNumber;
    },
    shouldShowAddFilingDateButton() {
      return this.isAdmin && !this.currentUserProfile?.application?.filingDate;
    },
    // Schedule Consultation
    scheduleConsultation() {
      router.push(`/schedule?uid=${this.currentUserProfile?.uid}`)
    },
    // Complete Consultation
    completeConsultation() {
      this.setTimelineStep(3)
    },
    // Go to supporting details
    goToSupportingDetails() {
      router.push(`/update-profile${this.isAdmin ? '?uid=' + this.currentUserProfile?.uid : ''}#supporting-details`)
    },
    // Clear conflict check
    clearConflictCheck() {
      this.setTimelineStep(2)
    },
    // Release engagement letter
    releaseEngagementLetter() {
      this.pending = true

      const to = this.currentUserProfile?.email
      const subject = 'Action required: sign your engagement letter'
      const url = `${process.env.VUE_APP_SITE_URL}/sign-engagement-letter`
      const message = `<h1>Congrats! You're on your way to your provisional patent.</h1><p>Please sign the engagement letter here: <a href="${url}">${url}</a>. Please verify the legal name of the person or business entity and verify your address details. Please note that a signed engagement letter is required to begin the patent process.</p>`

      sendEmail({ to, subject, message }).then(() => {
        this.setTimelineStep(4)
        this.pending = false
        this.$notify({
          group: 'default',
          title: 'Success',
          text: `Successfully sent message to ${to}`
        })
      }).catch((error) => {
        this.setTimelineStep(4)
        this.pending = false
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          type: 'error',
          text: error
        })
      })
    },
    // Sign engagement letter
    signEngagementLetter() {
      router.push(`/sign-engagement-letter?uid=${this.currentUserProfile?.uid}`)
    },
    // Charge card
    async chargeCard() {
      this.pending = true
      try {
        const paymentIntent = await fb.funcs.httpsCallable('createPaymentIntent')(this.currentUserProfile)

        const paymentConfirmationResponse = await fb.funcs.httpsCallable('confirmStripePayment')({
          paymentMethodId: this.currentUserProfile?.stripe.paymentMethodId,
          paymentIntentId: paymentIntent.data.id
        })
        this.pending = false
        const { status } = paymentConfirmationResponse?.data
        const supportingDetailsURL = `${process.env.VUE_APP_SITE_URL}/update-profile#supporting-details`
        const paymentPortalURL = `${process.env.VUE_APP_SITE_URL}/update-profile#payment`
        switch (status) {
          case 'succeeded':
            this.$notify({
              group: 'default',
              title: 'Success',
              text: `Your card was successfully charged`,
              type: 'success'
            })
            // Send email to user
            sendEmail({
              to: this.currentUserProfile?.email,
              subject: 'Action required: Submit supporting invention details',
              message: `<h1>Thank you for authorizing payment.</h1><p>Please upload your supporting documents, figures, and invention details via the following link: <a href="${supportingDetailsURL}">${supportingDetailsURL}</a>.</p>`
            }).catch(() => {})

            // Send email to admin
            sendEmail({
              to: this.adminEmail,
              subject: `${this.currentUserProfile?.firstName} ${this.currentUserProfile?.firstName} has authorized their credit card payment`,
              message: '<p>Next, they must log in to add supporting details</p>'
            }).catch(() => {})

            this.setTimelineStep(6)
            break
          case 'card_declined':
            this.$notify({
              group: 'default',
              title: 'Error',
              text: `Your payment method was declined. Please submit valid payment details in your profile`,
              type: 'error'
            })

            // Send email to admin
            await sendEmail({
              to: this.adminEmail,
              subject: 'User payment method declined.',
              message: `User ${this.currentUserProfile?.firstName} ${this.currentUserProfile?.lastName} (${this.currentUserProfile?.email}) payment declined`
            }).catch(() => {})
            // Send email to user
            sendEmail({
              to: this.adminEmail,
              subject: 'Action required: Your payment method was declined.',
              message: `<p>Your payment method was declined. Please submit valid payment details to proceed with your patent application. We cannot proceed until valid payment has been approved by your credit card provider.</p><p><a href="${paymentPortalURL}">${paymentPortalURL}</a></p>`
            }).catch(() => {})
            break
          case 'payment_intent_unexpected_state':
            this.$notify({
              group: 'default',
              title: 'Error',
              text: `This payment has already been processed`,
              type: 'error'
            })
            break
        }
      }
      catch(err) {
        this.$notify({
          group: 'default',
          title: 'Error',
          text: `Error charging card`,
          type: 'error'
        })
        this.pending = false
      }
    },
    // Edit profile
    editProfile() {
      router.push(`/update-profile?uid=${this.currentUserProfile?.uid}`)
    },
    // View submission details
    viewSubmissionDetails() {
      this.setTimelineStep(8)
      this.goToSupportingDetails()
    },
    // Release draft
    releaseDraft() {
      // Stop if there is no draft
      if (!this.currentUserProfile?.application?.draft) {
        this.$notify({
          duration: 10000,
          group: 'default',
          type: 'error',
          title: 'Error',
          text: 'A draft has not yet been uploaded. Do that first.'
        })
        return
      }

      const to = this.currentUserProfile?.email
      const subject = 'Action required: review and approve patent application draft'
      const url = `${process.env.VUE_APP_SITE_URL}/draft-feedback`
      const message = `<h1>The draft of your provisional patent application is ready for your review!</h1><p>Please review and approve of the draft here: <a href="${url}">${url}</a>. Please aim to submit your revisions, comments, and questions within 3 business days of receiving this message. If changes are needed, then we will review and incorporate your proposed edits and send you a revised draft for approval.</p>`

      sendEmail({ to, subject, message }).then(() => {
        this.setTimelineStep(9)
        this.pending = false
        this.$notify({
          group: 'default',
          title: 'Success',
          text: `Successfully sent message to ${to}`
        })
      }).catch((error) => {
        this.pending = false
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: error,
          type: 'error'
        })
      })
    },
    // Client review draft
    clientReviewDraft() {
      this.setTimelineStep(10)
    },
    // Add filing date
    addFilingDate() {
      // this.setTimelineStep(12)
      router.push(`/update-profile?uid=${this.currentUserProfile?.uid}#application`)
    },
    completeDraft() {
      this.setTimelineStep(11)
    },
    // Edit profile application tab
    editProfileApplicationTab() {
      router.push(`/update-profile?uid=${this.currentUserProfile?.uid}#application`)
    },
    // Report out to client: Sends email with Serial number, filing date, filing receipt, as-filed patent application
    async reportOutToClient() {
      const supportDetailsArray = Object.keys(this.currentUserProfile?.supportingDetails).map(k => this.currentUserProfile?.supportingDetails[k])
      if (supportDetailsArray.some(val => !val)) {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: 'The client does not yet have all supporting details filled out.',
          type: 'error'
        })
        return
      }
      if (!this.currentUserProfile?.application?.asFiled) {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: 'The client does not have an "as-filed" application uploaded to their profile yet. Do that first.',
          type: 'error'
        })
        return
      }

      // Load PDF
      this.pending = true
      const storageRef = fb.storage.ref()
      const fileRef = storageRef.child(`docs/${this.currentUserProfile?.uid}/${this.currentUserProfile?.application.asFiled}`)
      let downloadURL = ''
      await fileRef.getDownloadURL().then(url => {
        if (url) {
          downloadURL = url
        }
      })

      // const attachments = [{
      //   filename: 'file.pdf',
      //   data: downloadURL,
      //   contentType: 'application/pdf'
      // }]

      const to = this.currentUserProfile?.email
      const subject = 'Patent Pending! - Provisional patent application filed'
      const { filingDate, serialNumber } = this.currentUserProfile?.application
      const title = this.currentUserProfile?.supportingDetails?.inventionTitle?.toUpperCase()
      let message = `<h1>Congratulations! Your provisional patent application has been filed with the USPTO and is now "patent pending"${ filingDate ? ' as of ' + filingDate : '' }</h1>`
      message += `<p>You may refer to your provisional patent application as either:</p>`
      message += `<p>U.S. Provisional Patent Application No. ${serialNumber}, filed ${filingDate};</p>`
      message += `<p>-- OR --</p>`
      message += `<p>U.S. Provisional Patent Application No. ${serialNumber}, entitled "<em>${title}</em>," filed ${filingDate}.</p>`
      message += `<p>Congratulations again. We will contact you prior to the 1-year expiration of your provisional patent to ask if you wish to convert the provisional to a formal "non-provisional" patent application. However, you may reach out to us at any time if you wish to proceed with converting your patent application.</p>`
      message += `<h4>As-filed patent application: <a href="${downloadURL}">Download PDF</a></h4>`
      // message += `<p>If the above link doesn't work, copy and paste the following URL:</p>`
      // message += `<p>${downloadURL}</p>`
      message += `<p>Best regards,</p>`
      message += `<p>The PortlandPatent.com team</p>`

      sendEmail({ to, subject, message }).then(() => {
        this.pending = false
        this.$notify({
          group: 'default',
          title: 'Success',
          text: `Successfully sent message to ${to}`
        })
      }).catch((error) => {
        this.pending = false
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          type: 'error',
          text: error
        })
      })

      this.setTimelineStep(13)
    },
    // Client view draft
    clientViewDraft() {
      if (!this.currentUserProfile?.application.draft) {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: 'The client does not have a draft uploaded to their profile yet. Do that first.',
          type: 'error'
        })
        return
      }
      const storageRef = fb.storage.ref()
      const fileRef = storageRef.child(`docs/${this.currentUserProfile?.uid}/${this.currentUserProfile?.application.draft}`)
      fileRef.getDownloadURL().then(url => {
        if (url) {
          window.open(url, '_blank')
        }
      });
    },
    // Client approve draft
    clientApproveDraft() {
      // Send email to admin
      const to = this.adminEmail
      const subject = 'Action required: Application ready for submission - draft approved by user'
      const clientNumber = this.currentUserProfile?.clientNumber
      const message = `<p>${this.currentUserProfile?.firstName} ${this.currentUserProfile?.lastName} has approved the draft provisional patent application for client number ${clientNumber}. Please submit to the USPTO. Output relevant information including filing deadline, package type, and if expedited.</p>`

      sendEmail({ to, subject, message }).then(() => {
        this.setTimelineStep(10)
      }).catch((error) => {
        this.pending = false
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: error,
          type: 'error'
        })
      })
    },
    // Client submit feedback
    clientSubmitFeedback() {
      router.push('/draft-feedback')
    },
    // View booking details
    // viewBookingDetails() {
    // }
  }
}
</script>

<style lang="scss" scoped>
.task-group {
  position: relative;
  text-align: left;
  margin: 0 0 1rem 0;
  &__actions {
    button {
      @include btn-secondary;
      margin-bottom: 0.5rem;
      display: block;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  i {
    margin-right: 5px;
  }
  i.fa-check {
    color: $success;
  }
  i.unchecked {
    display: none;
    &::before {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      border: solid 2px #ccc;
      border-radius: .15rem;
      vertical-align: middle;
      z-index: 0;
    }
    i {
      position: relative;
    }
  }
  i.checked {
    background: url(~@/assets/img/icon-check.svg);
    width: 20px;
    height: 20px;
    background-size: contain;
    vertical-align: middle;
    float: right;
  }
  &__task {
    border: solid 2px $c-border;
    background: linear-gradient(45deg, #cecece, #e6ecf0);
    font-size: 90%;
    border-radius: 5rem;
    padding: 1.5rem;
    margin-bottom: 1rem;
    position: relative;

    &.current {
      background: $secondary-gradient;
      padding-left: 3.5rem;
      font-size: 100%;
      &::before {
        content: '';
        background: white url(~@/assets/img/fast-forward.svg) no-repeat;
        background-size: 32px;
        background-position: left 10px center;
        position: absolute;
        top: 4px;
        left: 4px;
        width: calc(100% - 8px);
        height: calc(100% - 8px);
        border-radius: 5rem;
      }
      &::after {
        border: solid 2px #cccccc;
        content: 'Next up...';
        display: block;
        position: absolute;
        top: -25px;
        left: 50px;
        font-size: 12px;
        text-transform: uppercase;
        background: #4193c2;
        background: linear-gradient(152deg, #4687c5, #4097c2);
        color: white;
        padding: 5px 10px;
        border-bottom: none;
        font-weight: bold;
        letter-spacing: .5px;
        border-top-left-radius: .5rem;
        border-top-right-radius: .5rem;
      }

      i, div {
        position: relative;
      }
    }
    &.complete {
      border: solid 1px #adadad;
      background: linear-gradient(45deg, #cecece, #e6ecf0);
      font-size: 90%;
    }
    &:last-child {
      margin-bottom: 0;
      &::after {
        display: none
      }
    }
  }
}
</style>